import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Search, Eye, Lock, Power, Trash2, Package, Loader2,
  ChevronUp, X,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsersTab({ adminToken }) {
  const navigate = useNavigate();
  const getHeaders = () => ({ Authorization: `Bearer ${adminToken}` });
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [passwordModal, setPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [assignModal, setAssignModal] = useState(null);
  const [assignPlaceId, setAssignPlaceId] = useState("");

  const fetchUsers = useCallback(async () => {
    try { const res = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${adminToken}` } }); setUsers(res.data.users || []); }
    catch (err) { if (err.response?.status === 401) window.location.reload(); }
  }, [adminToken]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const filteredUsers = users.filter(u => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) || (u.latest_business || "").toLowerCase().includes(q);
  });

  const handleViewDetails = async (userId) => {
    if (selectedId === userId) { setSelectedId(null); setDetails(null); return; }
    setSelectedId(userId); setDetailsLoading(true);
    try { const res = await axios.get(`${API}/admin/users/${userId}/details`, { headers: getHeaders() }); setDetails(res.data); }
    catch { toast.error("Failed"); }
    finally { setDetailsLoading(false); }
  };

  const handleChangePassword = async () => {
    if (!passwordModal || !newPassword || newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try { await axios.put(`${API}/admin/users/${passwordModal.id}/password`, { password: newPassword }, { headers: getHeaders() }); toast.success("Password changed"); setPasswordModal(null); setNewPassword(""); }
    catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Failed"); }
  };

  const handleToggleStatus = async (user) => {
    const newActive = !(user.active !== false);
    if (!window.confirm(`${newActive ? "activate" : "deactivate"} ${user.email}?`)) return;
    try { await axios.put(`${API}/admin/users/${user.id}/status`, { active: newActive }, { headers: getHeaders() }); toast.success(`User ${newActive ? "activated" : "deactivated"}`); fetchUsers(); }
    catch { toast.error("Failed"); }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Delete ${user.email} and ALL their data?`)) return;
    try { await axios.delete(`${API}/admin/users/${user.id}`, { headers: getHeaders() }); toast.success("User deleted"); setSelectedId(null); fetchUsers(); }
    catch { toast.error("Failed"); }
  };

  const handleAssignPackage = async () => {
    if (!assignModal || !assignPlaceId.trim()) { toast.error("Place ID required"); return; }
    try {
      await axios.post(`${API}/admin/users/${assignModal.id}/assign-package`, { place_id: assignPlaceId.trim(), package_id: "recommendation-report" }, { headers: getHeaders() });
      toast.success("Package assigned"); setAssignModal(null); setAssignPlaceId(""); fetchUsers();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Failed"); }
  };

  return (
    <div className="space-y-4">
      {/* Password Modal */}
      {passwordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { setPasswordModal(null); setNewPassword(""); }}>
          <div className="w-full max-w-md p-6 rounded-xl bg-white border border-slate-200" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4"><h3 className="font-semibold text-[#111827]">Change Password</h3><button onClick={() => { setPasswordModal(null); setNewPassword(""); }}><X className="w-4 h-4 text-[#6B7280]" /></button></div>
            <p className="text-sm mb-3 text-[#6B7280]">For <strong className="text-[#111827]">{passwordModal.email}</strong></p>
            <Input value={newPassword} onChange={e => setNewPassword(e.target.value)} type="password" placeholder="New password (min 6 chars)" className="mb-4 bg-white border-slate-300" data-testid="admin-new-password-input" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => { setPasswordModal(null); setNewPassword(""); }}>Cancel</Button>
              <Button size="sm" onClick={handleChangePassword} className="bg-blue-600 text-white" data-testid="admin-save-password-btn">Save Password</Button>
            </div>
          </div>
        </div>
      )}
      {/* Assign Modal */}
      {assignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { setAssignModal(null); setAssignPlaceId(""); }}>
          <div className="w-full max-w-md p-6 rounded-xl bg-white border border-slate-200" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4"><h3 className="font-semibold text-[#111827]">Assign Package</h3><button onClick={() => { setAssignModal(null); setAssignPlaceId(""); }}><X className="w-4 h-4 text-[#6B7280]" /></button></div>
            <p className="text-sm mb-3 text-[#6B7280]">Unlock a paid report for <strong className="text-[#111827]">{assignModal.email}</strong></p>
            <Input value={assignPlaceId} onChange={e => setAssignPlaceId(e.target.value)} placeholder="ChIJ..." className="mb-4 bg-white border-slate-300" data-testid="admin-assign-place-input" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => { setAssignModal(null); setAssignPlaceId(""); }}>Cancel</Button>
              <Button size="sm" onClick={handleAssignPackage} className="bg-emerald-600 text-white" data-testid="admin-assign-btn">Assign & Unlock</Button>
            </div>
          </div>
        </div>
      )}

      <Card className="bg-white border-[#E5E7EB]">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="text-lg text-[#111827]">Users ({filteredUsers.length})</CardTitle>
            <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" /><Input placeholder="Search name, email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 w-64 bg-white border-[#E5E7EB]" data-testid="user-search-input" /></div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-[#E5E7EB] bg-[#F9FAFB]">
                <TableHead className="text-[#6B7280]">User</TableHead>
                <TableHead className="text-[#6B7280]">Business</TableHead>
                <TableHead className="text-center text-[#6B7280]">Reports</TableHead>
                <TableHead className="text-center text-[#6B7280]">Status</TableHead>
                <TableHead className="text-[#6B7280]">Joined</TableHead>
                <TableHead className="text-right text-[#6B7280]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length > 0 ? filteredUsers.map(u => (
                <React.Fragment key={u.id}>
                  <TableRow className={`border-[#E5E7EB] ${selectedId === u.id ? "bg-blue-50/50" : ""}`}>
                    <TableCell><p className="font-medium text-sm text-[#111827]">{u.name || "—"}</p><p className="text-xs text-[#9CA3AF]">{u.email}</p><Badge variant="secondary" className="text-[10px] mt-0.5 bg-slate-100 text-slate-600">{u.auth_provider || "email"}</Badge></TableCell>
                    <TableCell>{u.latest_business ? <p className="text-sm text-[#111827]">{u.latest_business}</p> : <span className="text-[#9CA3AF]">—</span>}</TableCell>
                    <TableCell className="text-center"><span className="font-medium text-[#111827]">{u.report_count || 0}</span><span className="text-xs text-[#9CA3AF]"> / {u.purchase_count || 0} paid</span></TableCell>
                    <TableCell className="text-center"><Badge className={u.active !== false ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}>{u.active !== false ? "Active" : "Inactive"}</Badge></TableCell>
                    <TableCell className="text-sm text-[#6B7280]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleViewDetails(u.id)} className={selectedId === u.id ? "text-blue-500" : "text-[#6B7280]"}>{selectedId === u.id ? <ChevronUp className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</Button>
                        <Button variant="ghost" size="sm" onClick={() => { handleViewDetails(u.id); setAssignModal(u); }} className="text-emerald-500"><Package className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setPasswordModal(u)} className="text-amber-500"><Lock className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleToggleStatus(u)} className={u.active !== false ? "text-orange-500" : "text-emerald-500"}><Power className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {selectedId === u.id && (
                    <TableRow className="border-[#E5E7EB]">
                      <TableCell colSpan={6}>
                        {detailsLoading ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div> : details ? (
                          <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                            <div className="grid grid-cols-4 gap-3 mb-4">
                              {[{ label: "Reports", value: details.stats.total_reports, color: "blue" }, { label: "Paid", value: details.stats.paid_reports, color: "emerald" }, { label: "Spent", value: `$${details.stats.total_spent.toFixed(2)}`, color: "amber" }, { label: "Purchases", value: details.stats.total_purchases, color: "purple" }].map((s, i) => (
                                <div key={i} className="p-3 rounded-lg border border-slate-200 text-center"><p className="text-xs text-[#9CA3AF]">{s.label}</p><p className={`text-lg font-bold text-${s.color}-500`}>{s.value}</p></div>
                              ))}
                            </div>
                            {details.reports.length > 0 && <div className="space-y-2">{details.reports.map(r => (
                              <div key={r.id} className="flex items-center justify-between p-2.5 bg-white rounded border border-slate-200">
                                <div className="flex items-center gap-3"><p className="text-sm font-medium text-[#111827]">{r.business_name}</p><p className="text-xs text-[#9CA3AF]">{r.place_id?.slice(0, 15)}...</p></div>
                                <div className="flex items-center gap-2"><Badge className={r.score >= 70 ? "bg-emerald-50 text-emerald-600" : r.score >= 40 ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}>{r.score}/100</Badge><Badge className={r.status === "paid" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}>{r.status}</Badge></div>
                              </div>
                            ))}</div>}
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              )) : <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#9CA3AF]">No users found</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
